import React, { useEffect, useState } from "react";
import {
  Box,
  TextField,
  Typography,
  Divider,
  TableCell,
  Tooltip,
  Paper,
} from "@mui/material";
import CommonCalculator from "../../../builders/calculator/common";
import PdfGenerator from "./Pdf/DonationPdf";
import {
  formatAmount,
  formatHistoryAmount,
  isAutoload,
} from "../../../utils/hooks/dataHandler";
import ScheduleHeader from "../../App/ScheduleHeader";
import ScheduleNotes from "../ScheduleNotes";
import {
  TextFieldForName,
  TextFieldForViewOnly,
} from "../../utils/TextField/TextField";
import { CalculatorTextField } from "../../utils/TextField/NumericTextField";
import IconButtonWithTooltip from "../../utils/IconButtonWithTooltip";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { JustInfo } from "../../utils/Icon";
import ScheduleContainer from "../utils/ScheduleContainer";
import SectionBox from "../utils/SectionBox";
import CommonTable from "../../utils/CommonTable";

const columnSettings = [
  { field_type: "string", field: "approvedIPC", align: "left" },
  { field_type: "string", field: "details", align: "left" },
  { field_type: "numeric", field: "amount", align: "right" },
  { field_type: "string", field: "reference", align: "center" },
];

const DonationSchedule = ({
  company,
  schedule,
  scheduleDetails,
  saveSchedule,
  deleteSchedule,
  canEditSchedule = true,
}) => {
  const [schName, setSchName] = useState("Qualifying Donations");
  const [notes, setNotes] = useState("");
  const [reviewNotes, setReviewNotes] = useState("");
  const [donationRows, setDonationRows] = useState([]);
  const [sourceData, setSourceData] = useState([]);
  const [totalDonation, setTotalDonation] = useState(0);
  const [additionDeduction, setAdditionDeductions] = useState(250);
  const [additionDeductionAmount, setAdditionDeductionAmount] = useState(0);

  const handleSave = () => {
    schedule.name = schName;
    schedule.notes = notes;
    schedule.reviewNotes = reviewNotes;
    saveSchedule(schedule, {
      donationRows: donationRows,
      additionDeduction: additionDeduction,
      totalAdjustment: additionDeductionAmount,
      doc: JSON.stringify({
        donationRows,
        totalDonation,
        additionDeduction,
        additionDeductionAmount,
        notes,
        reviewNotes,
      }),
    });
  };

  const calculateAdditionDeductionAmount = (value, manual_td) => {
    let total = totalDonation;
    if (manual_td) total = manual_td;
    return Math.round(total * (value / 100));
  };

  const handleInputChange = (value, { index, field, field_type }) => {
    const updatedRows = [...donationRows];

    if (field === "amount") {
      updatedRows[index]["historyAmount"] = value;
      updatedRows[index][field] = value.reduce((sum, num) => sum + num, 0);

      let totalD = CommonCalculator.arraySum(updatedRows);
      setTotalDonation(totalD);
      let additionDeductionAmount = calculateAdditionDeductionAmount(
        additionDeduction,
        totalD
      );
      setAdditionDeductionAmount(additionDeductionAmount);
    } else {
      updatedRows[index][field] = value;
    }
    setDonationRows(updatedRows);
  };

  const handleAdditionDeduction = (value) => {
    if (value === "" || value >= 0) {
      setAdditionDeductions(value);

      let additionDeductionAmount = calculateAdditionDeductionAmount(value);
      setAdditionDeductionAmount(additionDeductionAmount);
    }
  };

  useEffect(() => {
    //init
    if (schedule) {
      setSchName(schedule.name);
      setNotes(schedule.notes);
      setReviewNotes(schedule.reviewNotes);
    }

    if (scheduleDetails) {
      setSourceData(scheduleDetails.sourceData);
      setDonationRows(
        scheduleDetails.donationRows.map((row) => {
          if (!row.historyAmount || row.historyAmount.length <= 0) {
            row.historyAmount = [row.amount];
          }
          return row;
        })
      );

      setAdditionDeductions(scheduleDetails.additionDeduction);
      //calculation
      let totalD = CommonCalculator.arraySum(scheduleDetails.donationRows);
      setTotalDonation(totalD);
      let c_additionDeduction = scheduleDetails.additionDeduction;
      setAdditionDeductionAmount(c_additionDeduction);
      const ad = calculateAdditionDeductionAmount(c_additionDeduction, totalD);
      setAdditionDeductionAmount(ad);
    }
  }, []);

  const addNewRow = (row = null) => {
    const newRow = {
      approvedIPC: "",
      details: "",
      amount: 888,
      reference: "",
    };
    if (row && row !== null) {
      newRow.approvedIPC = row.description;
      newRow.amount = row.amount;
      newRow.historyAmount = [row.amount];
      newRow.source = row.source?.sch_no || "";
    }
    const updatedData = [...donationRows];
    updatedData.push(newRow);
    setDonationRows(updatedData);
  };

  const removeRow = (rowIndex) => {
    let updatedRows = donationRows.filter((_, i) => i !== rowIndex);
    setDonationRows(updatedRows);

    //calculation
    let totalD = CommonCalculator.arraySum(updatedRows);
    setTotalDonation(totalD);
    let additionDeductionAmount = calculateAdditionDeductionAmount(
      additionDeduction,
      totalD
    );
    setAdditionDeductionAmount(additionDeductionAmount);
  };

  return (
    <Box sx={{ padding: 4 }}>
      <ScheduleHeader
        {...{
          title: schedule.type,
          companyName: company.name,
          companyType: company.companyType,
          uen: company.uen,
          yearOfAssessment: company.yearOfAssessment,
          financialYearStart: company.financialYearStart,
          financialYearEnd: company.financialYearEnd,
          companyType: company.companyType,
          handleSave,
          deleteSchedule,
          canEditSchedule,
        }}
      >
        <PdfGenerator
          schedule={schedule}
          company={company}
          donationRows={donationRows}
          totalDonation={totalDonation}
          additionDeduction={additionDeduction}
          additionDeductionAmount={additionDeductionAmount}
          notes={notes}
          reviewNotes={reviewNotes}
        />
      </ScheduleHeader>
      <br />

      <ScheduleContainer
        key={schedule.id}
        id={schedule.id}
        schedule={schedule}
        schName={schName}
        taxAdjustment={"Deduct"}
        allowedToEditSchName
        isSourceApplicable
        sourceData={sourceData}
        onImport={(row) => addNewRow(row)}
        updateHeading={(newTitle) => {
          schedule.name = newTitle;
          setSchName(newTitle);
        }}
        canEditSchedule={canEditSchedule}
      >
        {(fullScreenMode) => (
          <>
            <SectionBox
              title={schedule.type}
              onAdd={addNewRow}
              canEdit={canEditSchedule}
            >
              <CommonTable
                rows={donationRows}
                columns={[
                  {
                    key: "approvedIPC",
                    label: "Approved IPC",
                    headerAlign: "left",
                    render: (row, index) => (
                      <TextFieldForName
                        value={row?.approvedIPC || ""}
                        onChange={handleInputChange}
                        reference={{
                          index,
                          field: columnSettings[0].field,
                          field_type: columnSettings[0].field_type,
                        }}
                        disabled={!canEditSchedule}
                        canEdit={canEditSchedule}
                      />
                    ),
                  },
                  {
                    key: "details",
                    label: "Details",
                    headerAlign: "left",
                    render: (row, index) => (
                      <TextFieldForName
                        value={row?.details || ""}
                        onChange={handleInputChange}
                        reference={{
                          index,
                          field: columnSettings[1].field,
                          field_type: columnSettings[1].field_type,
                        }}
                        disabled={!canEditSchedule}
                        canEdit={canEditSchedule}
                      />
                    ),
                  },
                  {
                    key: "amount",
                    label: "Amount",
                    headerAlign: "right",
                    colAlign: "right",
                    render: (row, index) => (
                      <Tooltip
                        title={
                          row?.historyAmount &&
                          row.historyAmount.length > 0 &&
                          formatHistoryAmount(row.historyAmount)
                        }
                        placement='top'
                      >
                        <Box display='inline-block' sx={{ width: "100%" }}>
                          <CalculatorTextField
                            fullWidth={true}
                            value={row?.historyAmount || []}
                            onChange={handleInputChange}
                            reference={{
                              index: index,
                              field: columnSettings[2].field,
                              field_type: columnSettings[2].field_type,
                            }}
                            disabled={
                              row?.status === "autoload" || !canEditSchedule
                            }
                            canEdit={canEditSchedule}
                          />
                        </Box>
                      </Tooltip>
                    ),
                  },
                  {
                    key: "reference",
                    label: "Reference",
                    headerAlign: "center",
                    render: (row, index) => (
                      <TextFieldForName
                        value={row?.reference}
                        onChange={handleInputChange}
                        reference={{
                          index,
                          field: columnSettings[3].field,
                          field_type: columnSettings[3].field_type,
                        }}
                        disabled={!canEditSchedule}
                        canEdit={canEditSchedule}
                        viewOnlyAlign='center'
                      />
                    ),
                  },
                  {
                    key: "source",
                    label: "Sch",
                    headerAlign: "center",
                    render: (row) => (
                      <TextFieldForViewOnly
                        value={row.source || row.source?.sch_no || ""}
                      />
                    ),
                  },
                  canEditSchedule
                    ? {
                        key: "action",
                        label: "Action",
                        headerAlign: "center",
                        render: (row, index) => (
                          <IconButtonWithTooltip
                            icon={DeleteOutlineIcon}
                            tooltipTitle='Delete Row'
                            onClick={() => removeRow(index)}
                            color='error'
                            size='small'
                            disabled={
                              isAutoload(row.status) || !canEditSchedule
                            }
                          />
                        ),
                      }
                    : [],
                ]}
              >
                <TableCell></TableCell>
                <TableCell align='right' sx={{ fontWeight: "bold" }}>
                  Total Donations
                </TableCell>
                <TableCell align='right' sx={{ fontWeight: "bold" }}>
                  {formatAmount(totalDonation)}
                </TableCell>
                <TableCell colSpan={3}></TableCell>
              </CommonTable>
            </SectionBox>
            {/* Addition deductions */}
            <Paper
              elevation={0}
              sx={{
                p: 3,
                border: "1px solid",
                borderColor: "divider",
                bgcolor: "background.paper",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography
                variant='h6'
                sx={{
                  fontWeight: 600,
                  display: "flex",
                  alignItems: "center",
                  fontSize: { xs: "1rem", md: "1.15rem" },
                }}
              >
                Addition deductions
                <JustInfo title='This amount will be deducted back to the main computation.' />
                :&nbsp;&nbsp;&nbsp;
                {canEditSchedule ? (
                  <TextField
                    type='number'
                    value={additionDeduction}
                    onChange={(e) => handleAdditionDeduction(e.target.value)}
                    InputProps={{
                      inputProps: { min: 0, max: 100 },
                      disableUnderline: true,
                    }}
                    sx={{
                      mx: 1,
                      width: 70,
                      "& input": {
                        textAlign: "right",
                        fontWeight: 600,
                        fontSize: "1.1em",
                        bgcolor: "background.paper",
                        borderRadius: 1,
                        px: 1,
                        py: 0.5,
                        border: "1px solid",
                        borderColor: "divider",
                      },
                    }}
                    variant='standard'
                    disabled={!canEditSchedule}
                  />
                ) : (
                  <Typography
                    variant='h6'
                    sx={{
                      fontWeight: 600,
                      fontSize: { xs: "1rem", md: "1.15rem" },
                    }}
                  >
                    {additionDeduction}
                  </Typography>
                )}
                %
              </Typography>
              <Typography
                variant='h6'
                sx={{
                  fontWeight: 600,
                  fontSize: { xs: "1rem", md: "1.15rem" },
                }}
              >
                {formatAmount(additionDeductionAmount, true, true)}
              </Typography>
            </Paper>
          </>
        )}
      </ScheduleContainer>

      <Divider sx={{ marginY: 4 }} />

      <ScheduleNotes
        notes={notes}
        setNotes={setNotes}
        reviewNotes={reviewNotes}
        setReviewNotes={setReviewNotes}
        enableEdit={canEditSchedule}
      />
    </Box>
  );
};

export default DonationSchedule;
